import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { of, Subject, takeUntil, timer, forkJoin, Observable } from 'rxjs';
import { catchError, map, startWith, switchMap, take } from 'rxjs/operators';

import { Analysis, AnalysisProgress, AnalysisTableData } from '../../interfaces/analysis';
import { RowData } from '../../shared/interfaces/table.data';

import { AuthService } from '../../services/auth/auth.service';
import { AnalysisService } from '../../services/analysis-tab/analysis.service';
import { SvTestPannelService } from '../../services/sv-test-pannel/sv-test-panel.service';
import { MessageModalService } from '../../services/message-modal/message-modal.service';

import { TableSearchComponent } from '../../shared/table-search/table-search.component';
import { AnalysisInfoPanelComponent } from './info-panel/analysis-info-panel.component';
import { AnalysisEditModalComponent } from './edit-modal/analysis-edit-modal.component';
import { ModalDialogComponent } from '../../shared/modal-dialog/modal-dialog.component';
import { ButtonsComponent } from '../../shared/buttons/buttons.component';
import { SharePopupComponent } from '../../shared/share-popup/share-popup.component';
import { POLLING_INTERVAL } from '../../shared/constants/analysis-constants';

import { AnalysisTableComponent } from './analysis-table/analysis-table.component';
import { AnalysisContextActions, DownloadType, LoadingState } from './analysis.types';

import { environment } from '../../../environments/environment';
import { SampletestService } from '../../services/sampletest/sampletest.service';

@Component({
  selector: 'app-analysis',
  standalone: true,
  imports: [CommonModule, AnalysisTableComponent, TableSearchComponent, ButtonsComponent, AnalysisInfoPanelComponent],
  templateUrl: './analysis.component.html',
  styleUrl: './analysis.component.css',
  providers: [AnalysisService],
})
export class AnalysisComponent implements OnInit, OnDestroy {
  @ViewChild(AnalysisTableComponent) tableComponent?: AnalysisTableComponent;

  private rawAnalysisList: Analysis[] = [];
  selectedAnalysisItems: AnalysisTableData[] = [];
  userRole: string = 'none';

  dataSource: MatTableDataSource<RowData<AnalysisTableData>> = new MatTableDataSource<RowData<AnalysisTableData>>([]);

  private readonly ACTIVE_POLLING_STATUSES = ['submitted', 'analyzing'];
  private refreshPolling$ = new Subject<void>();
  rowStatusOverrides = new Map<number, string>();
  rowProgressMap = new Map<number, number>();

  shareBtnName: string = $localize`Share`;
  btnStyleFilledSmallForPrimaryBtn = 'btn-filled-small';
  btnStyleFilledSmallForPrimaryBtnDisabled = 'btn-filled-small-disabled';
  btnMatFlat: string = 'mat-flat-button';

  isInfoPanelOpen = false;
  selectedAnalysis: Analysis | null = null;

  /** Row with an open modal/panel (info, edit, or delete); drives the "active" row highlight. */
  activeRowAnalysisId: number | null = null;

  contextActionsMap = new Map<number, AnalysisContextActions>();

  private destroy$ = new Subject<void>();

  // Stable function references for @Input() bindings on the table wrapper
  readonly isViewResultDisabledFn = (row: AnalysisTableData): boolean => this.isViewResultDisabled(row);
  readonly isDownloadResultDisabledFn = (row: AnalysisTableData): boolean => this.isDownloadResultDisabled(row);
  readonly isPluginMenuDisabledFn = (row: AnalysisTableData): boolean => this.isPluginMenuDisabled(row);

  constructor(
    private router: Router,
    private analysisService: AnalysisService,
    private sampleService: SampletestService,
    private messageModal: MessageModalService,
    private dialog: MatDialog,
    private svTestPannelService: SvTestPannelService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.authService.authUserData$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.userRole = data?.userRole ?? 'none';
    });
    this.setupPolling();
    this.loadData();
  }

  loadData() {
    this.analysisService
      .getAnalyses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.setAnalysisListData(response.data);
          } else {
            this.messageModal.openModal($localize`Error`, response.message);
          }
        },
        error: () => {
          this.messageModal.openModal($localize`Error`, $localize`Error while fetching list of analyses.`);
        },
      });
  }

  setAnalysisListData(analysisListData: Analysis[]): void {
    this.rawAnalysisList = analysisListData;
    const rowData: RowData<AnalysisTableData>[] = analysisListData.map((item: Analysis) => ({
      data: {
        analysisId: item.AnalysisID,
        analysisName: item.AnalysisName,
        appName: item.Application,
        groupName: {
          list: item.Groups.sort((a, b) => (a.IsPrimary === b.IsPrimary ? 0 : a.IsPrimary ? -1 : 1)).map(
            (g) => g.GroupName
          ),
        },
        sampleName: item.SampleName,
        sampleId: String(item.SampleID),
        panelName: item.PanelName,
        status: item.Status,
        completedOn: item.CompletedOn,
        user: item.UserName,
        percentComplete: item.PercentComplete,
      },
      selected: false,
    }));
    this.dataSource = new MatTableDataSource<RowData<AnalysisTableData>>(rowData);
    this.dataSource.filterPredicate = this.createFilterPredicate();
    this.refreshPolling$.next();
  }

  createFilterPredicate(): (item: RowData<AnalysisTableData>, filter: string) => boolean {
    return (item: RowData<AnalysisTableData>, filter: string) => {
      const terms = filter.trim().toLowerCase().split(/\s+/);
      const data = item.data;
      const searchableString = [
        data.analysisName,
        data.sampleName,
        data.panelName,
        data.status,
        data.completedOn,
        data.user,
        ...(data.groupName.list || []),
      ]
        .filter((val) => val != null)
        .join(' ')
        .toLowerCase();

      return terms.every((term) => searchableString.includes(term));
    };
  }

  onFilterChanged(filterValue: string): void {
    this.dataSource.filter = filterValue;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  handleViewResult(analysisId: number): void {
    const viewResult = this.contextActionsMap.get(analysisId)?.viewResult;
    if (viewResult?.status === LoadingState.Ready && viewResult.url) {
      this.router.navigate([viewResult.url, analysisId]);
    } else {
      console.error(
        `Unknown error in view results state, link should be disabled. State: ${JSON.stringify(viewResult)}`
      );
    }
  }

  handleGetInfoClick(event: any): void {
    const analysisData: AnalysisTableData = event.data ?? event;
    const originalAnalysis = this.rawAnalysisList.find((a) => a.AnalysisID === analysisData.analysisId);

    if (!originalAnalysis) {
      return;
    }

    if (originalAnalysis.Status === 'completed') {
      this.fetchPluginsForAnalysis(originalAnalysis.AnalysisID);
      this.fetchResultsPageForAnalysis(originalAnalysis.AnalysisID);
    }

    this.selectedAnalysis = originalAnalysis;
    this.isInfoPanelOpen = true;
    this.activeRowAnalysisId = originalAnalysis.AnalysisID;
  }

  onInfoPanelClosed(): void {
    this.isInfoPanelOpen = false;
    this.activeRowAnalysisId = null;
  }

  handleEditClick(event: any): void {
    this.selectedAnalysisItems = [];
    this.tableComponent?.resetSelections();
    const rowData: AnalysisTableData = event.data ?? event;
    const originalAnalysis = this.rawAnalysisList.find((a) => a.AnalysisID === rowData.analysisId);
    if (!originalAnalysis) {
      return;
    }

    this.selectedAnalysis = originalAnalysis;
    this.activeRowAnalysisId = originalAnalysis.AnalysisID;

    const dialogRef = this.dialog.open(AnalysisEditModalComponent, {
      width: `60rem`,
      data: { analysisData: originalAnalysis },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.activeRowAnalysisId = null;
      if (result?.event === 'SUBMIT') {
        this.selectedAnalysis = null;
        this.analysisService
          .editAnalysis(result.analysisId, result.data)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageModal.openModal($localize`Success`, $localize`Analysis updated successfully.`);
              this.loadData();
            },
            error: () => {
              this.messageModal.openModal($localize`Error`, $localize`Failed to update analysis.`);
            },
          });
      } else if (result?.event === 'RELOAD') {
        this.selectedAnalysis = null;
        this.messageModal.openModal('Success', 'Analysis updated successfully.');
        this.loadData();
      }
    });
  }

  handleDeleteClick(event: any): void {
    this.selectedAnalysisItems = [];
    this.tableComponent?.resetSelections();
    const analysisData: AnalysisTableData = event.data ?? event;
    const originalAnalysis = this.rawAnalysisList.find((a) => a.AnalysisID === analysisData.analysisId);

    if (!originalAnalysis) {
      this.messageModal.openModal($localize`Error`, $localize`Could not find analysis data to delete.`);
      return;
    }

    this.activeRowAnalysisId = originalAnalysis.AnalysisID;

    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '450px',
      data: {
        title: $localize`Confirm Delete Analysis`,
        description: $localize`Are you sure you want to delete the analysis "<b>${originalAnalysis.AnalysisName}</b>"? This action cannot be undone.`,
        primaryButtonLabel: 'Delete',
        secondaryButtonLabel: 'Cancel',
        showPrimaryButton: true,
        showSecondaryButton: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.activeRowAnalysisId = null;
      if (result) {
        this.analysisService
          .deleteAnalysis(originalAnalysis.AnalysisID)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageModal.openModal($localize`Success`, $localize`Analysis deleted successfully.`);
              this.loadData();
            },
            error: (err) => {
              const errorMessage = err?.error?.message || 'Failed to delete the analysis. Please try again.';
              this.messageModal.openModal($localize`Error`, errorMessage);
            },
          });
      }
    });
  }

  handleDownloadResult({ type, analysisId }: { type: 'results' | 'logs' | 'raw'; analysisId: number }): void {
    this.selectedAnalysisItems = [];
    this.tableComponent?.resetSelections();
    this.analysisService
      .getSignedDownloadUrlForAnalysis(analysisId, type)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.signed_url) {
            this.downloadFile(response.signed_url);
          } else {
            this.messageModal.openModal($localize`Error`, $localize`Could not retrieve the download link.`);
          }
        },
        error: () => {
          this.messageModal.openModal($localize`Error`, `Failed to get the download link. Please try again.`);
        },
      });
  }

  private downloadFile(signedUrl: string): void {
    const a = document.createElement('a');
    a.href = signedUrl;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  handlePluginLaunched({ pluginId, analysisId }: { pluginId: string; analysisId: number }): void {
    // get related analysis IDs for inputs to plugin
    this.sampleService
      .getAnalysesForSample(String(this.rawAnalysisList.find((a) => a.AnalysisID === analysisId)?.SampleID))
      .pipe(take(1))
      .subscribe((analyses) => {
        const analysesForSample = analyses.reduce(
          (result, analysis) => {
            const inputs = analysis.inputs ?? 'undefined';
            result[inputs] = Number(analysis.analysisId);
            return result;
          },
          {} as Record<string, number>
        );

        // analysesForSample is of the form { "combine1p11": 1060, "remap1p11": 1059, "SV-Discovery": 1061 }
        this.router.navigate(['/plugin-viewer', pluginId], {
          queryParams: {
            analysis_id: analysisId,
            analyses_for_sample: encodeURIComponent(JSON.stringify(analysesForSample)),
          },
        });
      });
  }

  handleCheckboxChange(rowData: { data: AnalysisTableData; selected: boolean }): void {
    if (rowData.selected) {
      const exists = this.selectedAnalysisItems.some((item) => item.analysisId === rowData.data.analysisId);
      if (!exists) {
        this.selectedAnalysisItems.push(rowData.data);
      }
    } else {
      this.selectedAnalysisItems = this.selectedAnalysisItems.filter(
        (item) => item.analysisId !== rowData.data.analysisId
      );
    }
  }

  onShareClicked(): void {
    const selectedIds = this.selectedAnalysisItems.map((item) => item.analysisId);
    const dialogRef = this.dialog.open(SharePopupComponent, {
      width: '550px',
      data: {
        Ids: selectedIds,
        title: selectedIds.length == 1 ? 'Analysis' : 'Analyses',
        resource_path: 'analyses',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.selectedAnalysisItems = [];
        this.tableComponent?.resetSelections();
        this.loadData();
      }
    });
  }

  /**
   * Triggered when a row's "..." menu opens. Prefetches plugins and results
   * for the row if the analysis is complete.
   */
  onRowMenuOpened(row: AnalysisTableData): void {
    this.activeRowAnalysisId = row.analysisId;
    const status = this.getRowStatus(row);
    if (status !== 'completed') {
      return;
    }
    this.fetchPluginsForAnalysis(row.analysisId);
    this.fetchResultsPageForAnalysis(row.analysisId);
  }

  onRowMenuClosed(): void {
    // Only clear if the menu was dismissed without opening a row surface; if an item
    // opened the info panel or a dialog, that surface now owns the highlight and clears it.
    if (!this.isInfoPanelOpen && this.dialog.openDialogs.length === 0) {
      this.activeRowAnalysisId = null;
    }
  }

  private updateContextActions(analysisId: number, patch: Partial<AnalysisContextActions>): void {
    const existing = this.contextActionsMap.get(analysisId);
    this.contextActionsMap.set(analysisId, { ...existing, ...patch } as AnalysisContextActions);
  }

  private fetchPluginsForAnalysis(analysisId: number): void {
    const current = this.contextActionsMap.get(analysisId)?.plugins;
    if (current?.status === LoadingState.Loading || current?.status === LoadingState.Ready) {
      return;
    }

    this.updateContextActions(analysisId, { plugins: { status: LoadingState.Loading, items: [] } });
    this.analysisService
      .getPluginsForAnalysis(analysisId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.updateContextActions(analysisId, { plugins: { status: LoadingState.Ready, items } });
          this.cdr.markForCheck();
        },
        error: () => {
          this.updateContextActions(analysisId, { plugins: { status: LoadingState.Error, items: [] } });
          this.cdr.markForCheck();
        },
      });
  }

  private getPanelInputType(analysisId: number): Observable<string | null> {
    const originalAnalysis = this.rawAnalysisList.find((a) => a.AnalysisID === analysisId);
    if (!originalAnalysis) {
      console.error('Unable to get find full analysis data');
      return of(null);
    }

    return this.svTestPannelService.getPanelById(String(originalAnalysis.PanelID)).pipe(
      takeUntil(this.destroy$),
      map(({ success, data }) => {
        if (success && data) {
          return data.InputType.replace(/-/g, '').toLowerCase();
        } else {
          return null;
        }
      }),
      catchError((error) => {
        console.error(`Unable to fetch panel input type for analysis: ${error.message}`);
        return of(null);
      })
    );
  }

  /**
   * Analyses can have two types of results pages, or none, depending on completion status and panel type.
   * Panel input type === SV-Discovery => destination url is built-in HCE page
   * Other panel types => destination url is backend HTML page, if it exists, loaded as iframe
   * error condition (unable to find panel input type, no results page found, etc) => destination URL is null, leave link disabled
   */
  private fetchResultsPageForAnalysis(analysisId: number): void {
    const current = this.contextActionsMap.get(analysisId)?.viewResult;
    if (current?.status === LoadingState.Loading || current?.status === LoadingState.Ready) {
      return;
    }

    this.updateContextActions(analysisId, { viewResult: { status: LoadingState.Loading, url: null } });

    // fetch panel input type and determine if results page exists in parallel
    forkJoin({
      panelInputType: this.getPanelInputType(analysisId),
      reportResultsPageExists: this.analysisService.getAnalysisReportResourceExists(analysisId),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ panelInputType, reportResultsPageExists }) => {
          const hceIntegrationEnabled = environment.hceIntegrationEnabled ?? false;
          let viewResult: AnalysisContextActions['viewResult'];
          if (hceIntegrationEnabled && panelInputType === 'svdiscovery') {
            // HCE analysis page gets priority, if it's enabled in this environment
            viewResult = { status: LoadingState.Ready, url: '/analysis/hce' };
          } else if (reportResultsPageExists) {
            // otherwise, use HTML results page from back end
            viewResult = { status: LoadingState.Ready, url: '/analysis' };
          } else {
            // if neither is available, set error state and leave link disabled
            viewResult = { status: LoadingState.Ready, url: null };
          }
          this.updateContextActions(analysisId, { viewResult });
          this.cdr.markForCheck();
        },
        error: (err) => {
          // both this.getPanelInputType() and this.getAnalysisReportResourceExists() squash errors
          // set error state just in case
          console.error(`Unknown error determining analysis results page URL: ${err}`);
          this.updateContextActions(analysisId, { viewResult: { status: LoadingState.Error, url: null } });
          this.cdr.markForCheck();
        },
      });
  }

  isDownloadResultDisabled(row: AnalysisTableData): boolean {
    const status = this.getRowStatus(row);
    return status !== 'completed' && status !== 'error';
  }

  isViewResultDisabled(row: AnalysisTableData): boolean {
    if (this.getRowStatus(row) !== 'completed') {
      return true;
    }
    const viewResult = this.contextActionsMap.get(row.analysisId)?.viewResult;
    return !(viewResult?.status === LoadingState.Ready && viewResult.url != null);
  }

  isPluginMenuDisabled(row: AnalysisTableData): boolean {
    if (this.getRowStatus(row) !== 'completed') {
      return true;
    }
    return this.contextActionsMap.get(row.analysisId)?.plugins?.status !== LoadingState.Ready;
  }

  get selectedAnalysisDownloads(): DownloadType[] {
    const status = this.selectedAnalysis?.Status?.toLowerCase();
    if (status !== 'completed' && status !== 'error') {
      return [];
    }

    const userHasFullAccess = ['Admin', 'Manager'].includes(this.userRole);
    if (status === 'error') {
      return userHasFullAccess ? ['logs'] : [];
    }

    return userHasFullAccess ? ['results', 'logs', 'raw'] : ['results'];
  }

  getRowStatus(row: AnalysisTableData | null): string {
    if (!row) {
      return '';
    }
    return (this.rowStatusOverrides.get(row.analysisId) || row.status || '').toLowerCase();
  }

  private getActiveAnalysisIds(): number[] {
    return this.dataSource.data
      .filter(({ data: analysis }) => {
        if (!analysis.analysisId) return false;
        return this.ACTIVE_POLLING_STATUSES.includes(this.getRowStatus(analysis));
      })
      .map(({ data: analysis }) => analysis.analysisId);
  }

  private setupPolling(): void {
    this.refreshPolling$
      .pipe(
        startWith({}),
        switchMap(() =>
          timer(0, POLLING_INTERVAL).pipe(
            switchMap(() => {
              const activeAnalysisIds = this.getActiveAnalysisIds();
              if (activeAnalysisIds.length > 0) {
                return this.analysisService.getAnalysisProgressMultiple(activeAnalysisIds);
              }

              return of([]);
            }),
            takeUntil(this.destroy$)
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((progressUpdates: AnalysisProgress[]) => {
        progressUpdates.forEach((update) => {
          const analysisId = update.AnalysisID;

          if (update.Status.toLowerCase() === 'completed') {
            this.analysisService
              .getAnalysisDetails(analysisId)
              .pipe(takeUntil(this.destroy$))
              .subscribe((response) => {
                const fullAnalysisData = response.data;
                const rowToUpdate = this.dataSource.data.find((row) => row.data.analysisId === analysisId);
                if (rowToUpdate) {
                  rowToUpdate.data.status = fullAnalysisData.Status;
                  rowToUpdate.data.completedOn = fullAnalysisData.CompletedOn;
                  rowToUpdate.data.percentComplete = 100;
                  this.dataSource.data = [...this.dataSource.data];
                }
              });
          }

          this.rowStatusOverrides.set(analysisId, update.Status);
          this.rowProgressMap.set(analysisId, update.PercentComplete ?? 0);
        });

        if (progressUpdates.length > 0) {
          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
